import React from "react";
import { useI18n } from "@/lib/i18n";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const CONTENT = {
  it: {
    overline: "Scienza e Qualità",
    title: "La scienza del benessere, naturalmente.",
    lead:
      "Il nostro impegno si concentra su ingredienti selezionati, formulazioni prudenti e produzione tracciata. Le pagine tecniche e le certificazioni vengono aggiornate dall'amministratore quando disponibili.",
    section_intro: "Come lavoriamo, un passo alla volta.",
    sections: [
      {
        title: "Il nostro approccio",
        body: "Il nostro lavoro parte da un principio semplice: la trasparenza. Ogni ingrediente, ogni scelta di formulazione, ogni indicazione d'uso è progettata per offrire chiarezza a chi sceglie BioNutrix — che sia un consumatore, un professionista o un rivenditore.",
      },
      { title: "Selezione degli ingredienti", body: "Selezioniamo materie prime tra fornitori con esperienza consolidata, valutando la coerenza tra dati tecnici, tracciabilità e destinazione d'uso." },
      { title: "Sviluppo delle formulazioni", body: "Le formulazioni nascono dal confronto tra team interno e specialisti esterni. Ogni dosaggio è coerente con la destinazione d'uso e con la letteratura consultata." },
      { title: "Controllo e produzione", body: "I prodotti sono realizzati in Italia. La qualità è controllata lungo tutta la filiera, dalle materie prime al lotto finito." },
      { title: "Tracciabilità", body: "Ogni lotto è tracciato e conserva un riferimento ai controlli effettuati." },
      { title: "Documentazione e trasparenza", body: "L'area amministrativa del sito è predisposta per pubblicare, quando disponibili, schede tecniche, etichette e riferimenti bibliografici." },
    ],
    cert_over: "Certificazioni",
    cert_title: "Le nostre certificazioni",
    cert_lead:
      "Questa sezione mostrerà le certificazioni e le documentazioni ufficiali una volta caricate dall'amministratore. Non pubblichiamo loghi o attestati non ancora verificati.",
    cert_empty: "Documenti in fase di caricamento. Contattaci per informazioni.",
    faq_title: "FAQ sulla qualità",
    faqs: [
      { q: "I prodotti BioNutrix sono farmaci?", a: "No. I prodotti Pet attualmente commercializzati sono classificati come mangimi complementari e non come farmaci veterinari." },
      { q: "Dove trovo le etichette ufficiali?", a: "Le etichette ufficiali sono riportate sulle confezioni. Nell'area amministrativa del sito è previsto un caricamento specifico da parte del responsabile qualità." },
      { q: "Vengono effettuati studi clinici sui prodotti?", a: "Al momento non pubblichiamo studi clinici. Ogni informazione riportata sul sito è verificata rispetto alla documentazione disponibile e alla normativa vigente." },
    ],
  },
  en: {
    overline: "Science & Quality",
    title: "The science of wellbeing, naturally.",
    lead:
      "Our commitment focuses on selected ingredients, cautious formulations and traceable production. Technical pages and certifications are updated by the administrator when available.",
    section_intro: "How we work, one step at a time.",
    sections: [
      { title: "Our approach", body: "Our work starts from a simple principle: transparency. Every ingredient, every formulation choice, every usage indication is designed to offer clarity to those who choose BioNutrix — consumer, professional or reseller." },
      { title: "Ingredient selection", body: "We select raw materials from experienced suppliers, evaluating the consistency between technical data, traceability and intended use." },
      { title: "Formulation development", body: "Our formulations arise from the exchange between an internal team and external specialists. Every dosage is consistent with the intended use and with the literature reviewed." },
      { title: "Control and production", body: "Products are made in Italy. Quality is controlled along the entire supply chain, from raw materials to the finished batch." },
      { title: "Traceability", body: "Every batch is traced and retains a reference to the checks carried out." },
      { title: "Documentation and transparency", body: "The site's admin area is set up to publish, when available, technical sheets, labels and bibliographic references." },
    ],
    cert_over: "Certifications",
    cert_title: "Our certifications",
    cert_lead:
      "This section will display certifications and official documents once the administrator uploads them. We do not publish logos or attestations that have not yet been verified.",
    cert_empty: "Documents being uploaded. Contact us for more information.",
    faq_title: "Quality FAQ",
    faqs: [
      { q: "Are BioNutrix products medicines?", a: "No. The Pet products currently marketed are classified as complementary feed and not as veterinary medicines." },
      { q: "Where can I find the official labels?", a: "Official labels are printed on the packaging. The admin area of the site is set up for a specific upload by the quality manager." },
      { q: "Are clinical studies performed on the products?", a: "At the moment we do not publish clinical studies. Every piece of information on the site is verified against available documentation and applicable regulations." },
    ],
  },
};

export default function ScienceQualityPage() {
  const { lang } = useI18n();
  const c = CONTENT[lang];
  return (
    <div data-testid="science-quality-page">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/3039569/pexels-photo-3039569.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt=""
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10 pt-16 lg:pt-24 pb-16">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-editorial text-muted-ink mb-4">{c.overline}</div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-brand leading-[0.98]">{c.title}</h1>
            <p className="mt-6 text-lg text-muted-ink max-w-2xl">{c.lead}</p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <h2 className="font-display text-4xl text-brand leading-tight sticky top-24">{c.section_intro}</h2>
          </div>
          <div className="md:col-span-8 space-y-10">
            {c.sections.map((s, i) => (
              <div key={s.title} className="border-t border-soft pt-8">
                <div className="text-xs text-muted-ink tracking-editorial uppercase mb-2">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="font-display text-2xl md:text-3xl text-brand mb-3">{s.title}</h3>
                <p className="text-muted-ink leading-relaxed max-w-2xl">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-cream-warm py-16 lg:py-20">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="text-xs uppercase tracking-editorial text-muted-ink mb-4">{c.cert_over}</div>
          <h2 className="font-display text-4xl text-brand leading-tight mb-6">{c.cert_title}</h2>
          <p className="text-muted-ink max-w-2xl mb-8">{c.cert_lead}</p>
          <div className="border border-dashed border-soft p-10 bg-white text-center">
            <p className="text-sm text-muted-ink italic">{c.cert_empty}</p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-16 lg:py-20">
        <h2 className="font-display text-4xl text-brand leading-tight mb-8">{c.faq_title}</h2>
        <Accordion type="single" collapsible className="max-w-3xl">
          {c.faqs.map((f, i) => (
            <AccordionItem key={i} value={`sq-${i}`} className="border-b border-soft">
              <AccordionTrigger className="text-brand hover:no-underline text-left">{f.q}</AccordionTrigger>
              <AccordionContent className="text-muted-ink text-sm">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </div>
  );
}
